import { Alert, AlertDescription, Button } from '@invent/wl-ui-kit-next/ui';
import { Plus, SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactDivider,
  CompactMenu,
  CompactTitle,
  DataTable,
  HelpPopover,
  PageHeader,
  SplitList,
  STICKY_TABLE_TOP,
  StatusChip,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type IDataColumn,
  WorkControls,
  openRow,
  PaneControl,
  RowOpens,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { ISavedViewSummary } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, PastTheEnd, useAdminData } from '../../components/admin-page';
import { ListArea, useListRereads, usePaneRead, wholePage } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import { ALL_SURVEYS, countOf, LIST_SURVEYS, MIGRATED_MARK, listSurveyLabel, visibilityLabel } from '../../lists';
import { NEW_REPORT, TEAM_PATHS, reportPath } from '../../paths';
import { PageAction } from '../lists/work-page/light-list';
import { SurveyTabs } from '../lists/area';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import { WorkPager, dropPaging, useWorkPaging } from '../lists/work-page/pager';
import { RecordSearch } from '../record-lists/record-work-page';
import { VisibilityIcon, viewOwner } from '../lists/view-picker';

/**
 * Reports (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §4.1) — a
 * survey's report views, `GET /views?survey=&kind=report`, under `responses.browse` like Lists: the
 * survey tabs of Lists (`?survey=`, FA when absent, *All surveys* included), a table of the reports
 * the caller may open — Name (Lists' visibility icon before it) · Description · Visibility · Owner ·
 * Updated, in survey-api's order
 * (the opened first, newest first, then by name) — and **New report**, a blank one of the tab's
 * survey (`/team/reports/new?survey=`). A row opens the report beside the list (`AdminReportsArea`).
 *
 * **A light work page** (SR-515; `docs/superpowers/specs/2026-10-28-work-pages-design.md`, "Three
 * forms"): the title with the survey tabs and "N reports", *Refresh* in the app bar, the "?" holding
 * the explanations, the search and the page's buttons on one toolbar, the pager, and the
 * compact bar once scrolled. No cards and no counts.
 *
 * **A survey this deployment does not hold** (the views' 404; `local` has no Canada) says so. The
 * owner is the email, **Seeded** for a seed; a report that came from Salesforce is marked "From
 * Salesforce" beside its name, as in Lists' *Select a view* — what it lost is said on the report
 * itself, the list carrying the flag alone (`migrated`, not `migratedFrom`).
 *
 * **The search** (SR-462, `?q=`) finds reports of every survey by name and description as typed —
 * survey-api's list without `survey` holds them all, read whole and filtered here — each row tagged
 * with its survey and opened in it (`?survey=` its own, the search kept). A tab is browsing again:
 * it clears the search. The pager pages that one read; the pane beside an open report reads the
 * same.
 */
export function AdminReportsPage() {
  const [params, setParams] = useSearchParams();
  const survey = params.get('survey') || 'FA';
  const q = params.get('q') ?? '';
  const can = useCan();
  const location = useLocation();
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const rereads = useListRereads();
  const searching = q.trim() !== '';
  // Searching reads every survey's reports once, whatever the tab; browsing reads the tab's.
  const asked = searching ? '' : survey;
  const { data, error, reading, reload } = useAdminData(
    async (signal) => ({ asked, views: await listsApi.listViews(asked, 'report', signal) }),
    [asked, rereads]
  );
  const answer = data?.asked === asked ? data : null;
  // An error stands only while no newer read is out.
  const failed = error !== null && !reading;
  const readAt = useReadAt(data);
  const searchOf = (code: string) => `?${new URLSearchParams({ survey: code, ...(q ? { q } : {}) })}`;
  const search = searchOf(survey);
  const found = answer?.views ? answer.views.filter((view) => matchesReport(view, q)) : null;
  const paging = useWorkPaging({ replace: true });
  const { page, perPage, pages } = paging;
  const shown = found?.slice((page - 1) * perPage, (page - 1 + pages) * perPage) ?? null;
  // Sending the search on screen reads again: a report made a moment ago is found.
  const onSearch = (text: string) => {
    if (text === q) {
      void reload();
      return;
    }
    setParams(
      (now) => {
        const next = dropPaging(new URLSearchParams(now));
        if (text.trim()) next.set('q', text);
        else next.delete('q');
        return next;
      },
      { replace: true }
    );
  };
  const deleted = (location.state as Nullable<{ deletedReport?: string }>)?.deletedReport;
  // The list's address — its window included — with the report's own survey.
  const beside = (view: ISavedViewSummary) =>
    openRecord(reportPath(view.id), { search: paneSearch(location.search, view.survey), list: true } satisfies ICardLinkState);
  // A row's press follows *Opens in*: beside the list, or the report in a new tab.
  const prefs = usePanePrefs();
  const openReport = (view: ISavedViewSummary) => openRow(prefs, reportPath(view.id), () => beside(view));

  const total = found?.length ?? null;
  const refresh = { reading, readAt, onClick: () => void reload() };
  const help = (
    <HelpPopover label="About Reports" align="end">
      <p>
        A survey&apos;s saved reports — the ones shared with everyone, and your own. New report
        starts a blank one of the survey whose tab is open.
      </p>
      <p>
        A row opens beside the list by its first icon and in a new tab by ↗; a click on the row goes
        where «Opens in» says.
      </p>
      <p>
        The search reads as you type and finds reports of every survey by name and description,
        each tagged with its survey. A survey tab is browsing again, and clears the search.
      </p>
    </HelpPopover>
  );
  const offered = answer?.views !== null;
  const newReport = offered && (
    <PageAction
      label="New report"
      icon={<Plus className="size-4" aria-hidden />}
      to={reportPath(NEW_REPORT, 'definition', search)}
      state={{ search, list: true } satisfies ICardLinkState}
    />
  );
  const fields = offered && can('internal_fields.author') && (
    <Button asChild size="sm" variant="outline">
      <Link to={TEAM_PATHS.reportFields}>
        <SlidersHorizontal className="size-4" aria-hidden />
        Fields
      </Link>
    </Button>
  );
  const searchBox = (compact: boolean) => (
    <RecordSearch
      q={q}
      label="Filter reports"
      placeholder="Filter reports…"
      onSearch={onSearch}
      compact={compact}
    />
  );
  const surveys = [...LIST_SURVEYS, ALL_SURVEYS];
  const openSurvey = searching ? null : (surveys.find((tab) => tab.code === survey) ?? null);
  const toolbar = useWorkToolbar();
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  return (
    <>
      <PageHeader
        title="Reports"
        row
        tabs={<SurveyTabs current={searching ? null : survey} />}
        actions={
          <WorkPageHeaderActions
            summary={total === null ? null : countOf(total, 'report')}
            summaryData={{ 'data-reports-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Reports, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
      >
        <CompactTitle>Reports</CompactTitle>
        <CompactMenu
          label="Survey"
          trigger={openSurvey?.label ?? 'All surveys searched'}
          items={surveys.map((tab) => ({
            key: tab.code,
            label: tab.label,
            to: { search: `?${new URLSearchParams({ survey: tab.code })}` },
            current: tab === openSurvey
          }))}
        />
        <CompactDivider />
        {searchBox(true)}
        <span className="flex-1" />
        <CompactDivider />
        {fields}
        {newReport}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {deleted && (
          <Alert>
            <AlertDescription>The report {deleted} was deleted, with its runs.</AlertDescription>
          </Alert>
        )}
        {answer?.views === null ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            This survey is not on this deployment.
          </p>
        ) : (
          <>
            <WorkControls end={setControlsEnd}>
              <WorkToolbar
                toolbar={toolbar}
                status={{ reading, count: found ? `${found.length.toLocaleString('en-US')} found` : undefined }}
                search={searchBox(false)}
                quick={null}
                quickCount={0}
                end={
                  <>
                    {fields}
                    {newReport}
                    <PaneControl prefs={prefs} kind="record" />
                  </>
                }
              />
            </WorkControls>
            {failed && answer !== null && (
              <LoadError message={`Could not refresh — showing the reports as last read. ${error}`} />
            )}
            <DataTable
              label={searching ? 'Reports found' : `${listSurveyLabel(survey)} reports`}
              columns={[openColumn(beside), ...(searching ? SEARCH_COLUMNS : REPORT_COLUMNS)]}
              rows={shown === null || shown.length === 0 ? null : shown}
              rowKey={(view) => view.id}
              rowProps={(view) => ({ 'data-view': view.id }) as React.HTMLAttributes<HTMLTableRowElement>}
              onOpen={openReport}
              busy={reading && answer !== null}
              state={
                found !== null && found.length > 0 && shown !== null && shown.length === 0
                  ? {
                      kind: 'empty',
                      content: (
                        <PastTheEnd
                          page={page}
                          pageSize={perPage}
                          total={found.length}
                          counted={['report', 'reports']}
                          onPage={paging.setPage}
                        />
                      )
                    }
                  : reportsState(answer === null, failed ? error : null, survey, q)
              }
              stickyTop={STICKY_TABLE_TOP}
            />
            {found && <WorkPager {...paging.pager} total={found.length} busy={reading} sticky />}
          </>
        )}
      </div>
    </>
  );
}

/** A report's survey, as a tag beside its name where the list holds several surveys. */
function SurveyTag({ survey }: { survey: string }) {
  return <StatusChip className="shrink-0">{listSurveyLabel(survey)}</StatusChip>;
}

/** A report's name, its visibility's icon before it and "From Salesforce" after it where it came
 *  from there — as Lists' views are listed. Inline, so the pane's one line still cuts the name
 *  with "…". */
function ReportName({ view }: { view: ISavedViewSummary }) {
  return (
    <span>
      <VisibilityIcon visibility={view.visibility} className="mr-1.5 inline-block align-[-0.125rem]" />
      {view.name}
      {view.migrated && (
        <StatusChip className="ml-1.5 align-[0.0625rem] font-normal" data-migrated="">
          {MIGRATED_MARK}
        </StatusChip>
      )}
    </span>
  );
}

const REPORT_COLUMNS: IDataColumn<ISavedViewSummary>[] = [
  { id: 'name', header: 'Name', className: 'font-medium', cell: (view) => <ReportName view={view} /> },
  {
    id: 'description',
    header: 'Description',
    cell: (view) => view.description ?? <span className="text-muted-foreground">None</span>
  },
  { id: 'visibility', header: 'Visibility', cell: (view) => visibilityLabel(view.visibility) },
  { id: 'owner', header: 'Owner', cell: viewOwner },
  { id: 'updated', header: 'Updated', className: 'whitespace-nowrap', cell: (view) => formatDateTime(view.updatedAt) }
];

/** The ways in first on a row: the report beside the list, and ↗ in a new tab. */
function openColumn(beside: (view: ISavedViewSummary) => void): IDataColumn<ISavedViewSummary> {
  return {
    id: 'open',
    header: <span className="sr-only">Open</span>,
    label: 'Open',
    className: 'w-0 whitespace-nowrap',
    cell: (view) => (
      <RowOpens
        name={view.name}
        onPane={() => beside(view)}
        to={reportPath(view.id)}
        tabWords={`Open ${view.name} in a new tab`}
      />
    )
  };
}

/** The search's columns: the survey's tag beside the name, the rest as the tab draws them. */
const SEARCH_COLUMNS: IDataColumn<ISavedViewSummary>[] = [
  {
    id: 'name',
    header: 'Name',
    className: 'font-medium',
    cell: (view) => (
      <span className="flex items-center gap-2">
        <SurveyTag survey={view.survey} />
        <ReportName view={view} />
      </span>
    )
  },
  ...REPORT_COLUMNS.slice(1)
];

/** Reports and a report, as one area: the list, or a report beside it. */
export function AdminReportsArea() {
  return (
    <ListArea
      param="viewId"
      list={TEAM_PATHS.reports}
      paneTitle="Reports"
      pane={(search, selected) => <ReportsPane search={search} selected={selected} />}
      columnFrom={1280}
    />
  );
}

/** The reports table's state: the read out, failed, or answered with none. */
function reportsState(unread: boolean, error: Nullable<string>, survey: string, q: string): DataTableState {
  if (unread && error) {
    return { kind: 'error', content: <LoadError message={`The reports could not be read. ${error}`} /> };
  }
  if (unread) return { kind: 'loading' };
  if (q.trim()) return { kind: 'empty', content: `No report matches “${q.trim()}”.` };
  return { kind: 'empty', content: `No ${listSurveyLabel(survey)} report yet — New report makes one.` };
}

/** Whether a report's name or description holds every word of the search, ignoring case. */
export function matchesReport(view: ISavedViewSummary, q: string): boolean {
  const text = `${view.name} ${view.description ?? ''}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => text.includes(word));
}

/** The list's address with a report's own survey in it, the search kept. */
function paneSearch(search: string, survey: string): string {
  const next = new URLSearchParams(search);
  next.set('survey', survey);
  return `?${next}`;
}

/** The survey's reports as the pane beside one draws them — the whole list, one page; a search's
 *  reports of every survey. */
function ReportsPane({ search, selected }: { search: string; selected: string }) {
  const searching = (new URLSearchParams(search).get('q') ?? '').trim() !== '';
  const { shown, status } = usePaneRead(search, 'No report yet.', async (params, _page, signal) => {
    const q = params.get('q') ?? '';
    const views = (await listsApi.listViews(q.trim() ? '' : params.get('survey') || 'FA', 'report', signal)) ?? [];
    return wholePage(views.filter((view) => matchesReport(view, q)));
  });
  return (
    <SplitList
      label="Reports"
      selected={shown?.items.some((view) => view.id === selected) ? selected : null}
      items={
        shown?.items.map((view) => ({
          key: view.id,
          to: reportPath(view.id),
          state: { search: paneSearch(search, view.survey), list: true } satisfies ICardLinkState,
          primary: <ReportName view={view} />,
          secondary: [...(searching ? [listSurveyLabel(view.survey)] : []), visibilityLabel(view.visibility), viewOwner(view)].join(' · ')
        })) ?? null
      }
      status={status}
    />
  );
}
